import { htmlDecode, nFormatter, isTouchDevice } from './util';
import { initialColors } from './colors';

const OEC_PREFIX = /^OEC::/;

const Treemaps = {
  init() {
    window.google.charts.load('current', { packages: ['treemap'] }).then(() => {
      this.drawTreemaps();
    });
  },
  drawTreemaps() {
    const queryOptions = {
      sendMethod: 'auto',
    };

    const containers = document.querySelectorAll('.js-treemap');

    containers.forEach((container) => {
      const dataSource = container.getAttribute('data-source');
      const dataSourceType = container.getAttribute('data-source-type');
      const chartOptions = container.getAttribute('data-chart-options');
      const options = chartOptions ? JSON.parse(chartOptions) : {};

      if (dataSource) {
        if (OEC_PREFIX.test(dataSourceType)) {
          const url = htmlDecode(dataSource);

          fetch(url, { credentials: 'include' })
            .then((response) => {
              if (!response.ok) {
                return Promise.reject('Error status: ' + response.status);
              }
              return response.json();
            })
            .then((data) => {
              if (data.data && data.data.length) {
                if (
                  dataSourceType == 'OEC::market_growth_exports' ||
                  dataSourceType == 'OEC::market_growth_imports'
                ) {
                  const countries = [];
                  const filteredData = [];
                  let valueColumnLabel = '';

                  if (dataSourceType == 'OEC::market_growth_exports') {
                    valueColumnLabel = 'Exports Value Growth';
                  } else if (dataSourceType == 'OEC::market_growth_imports') {
                    valueColumnLabel = 'Imports Value Growth';
                  }

                  //Select top 10 countries
                  const _data = data.data
                    .sort(
                      (a, b) =>
                        b['Trade Value Growth Value'] -
                        a['Trade Value Growth Value'],
                    )
                    .slice(0, 10);

                  for (let i = 0; i < _data.length; i++) {
                    const currentCountryData = _data[i];

                    if (
                      currentCountryData['Trade Value Growth Value'] &&
                      countries.indexOf(currentCountryData['Country ID'] < 0)
                    ) {
                      countries.push(currentCountryData['Country ID']);
                      filteredData.push(currentCountryData);
                    }
                  }

                  const dataTable = new window.google.visualization.DataTable();
                  dataTable.addColumn('string', 'Country');
                  dataTable.addColumn('string', 'Parent');
                  dataTable.addColumn('number', valueColumnLabel);

                  dataTable.addRow(['World', null, 0]);

                  filteredData.forEach((country) => {
                    dataTable.addRow([
                      country['Country'],
                      'World',
                      parseInt(country['Trade Value Growth Value']),
                    ]);
                  });

                  Treemaps.drawChart(
                    dataTable,
                    {
                      ...options,
                      maxDepth: 2,
                      valueFormatter: function (value) {
                        return nFormatter(value, 2, '$');
                      },
                    },
                    container.querySelector('.chart__wrapper'),
                  );
                } else if (dataSourceType == 'OEC::export_destinations') {
                  const dtExportDest = new window.google.visualization.DataTable();
                  dtExportDest.addColumn('string', 'Country');
                  dtExportDest.addColumn('string', 'Parent');
                  dtExportDest.addColumn('number', 'Exports Value');

                  dtExportDest.addRow(['World', null, 0]);

                  const edData = data.data
                    .sort((a, b) => b['Trade Value'] - a['Trade Value'])
                    .slice(0, 15);

                  edData.forEach((row) => {
                    dtExportDest.addRow([
                      row['Importer Country Official'] || row['Country'] || 'Unknown',
                      'World',
                      row['Trade Value'] || 0,
                    ]);
                  });

                  Treemaps.drawChart(
                    dtExportDest,
                    {
                      ...options,
                      maxDepth: 2,
                      valueFormatter: function (value) {
                        return nFormatter(value, 2, '$');
                      },
                    },
                    container.querySelector('.chart__wrapper'),
                  );
                } else if (dataSourceType == 'OEC::exported_products') {
                  const dtExportProd = new window.google.visualization.DataTable();
                  dtExportProd.addColumn('string', 'Product');
                  dtExportProd.addColumn('string', 'Parent');
                  dtExportProd.addColumn('number', 'Exports Value');

                  const epData = data.data
                    .sort((a, b) => b['Trade Value'] - a['Trade Value'])
                    .slice(0, 20);

                  dtExportProd.addRow(['All Products', null, 0]);

                  epData.forEach((row) => {
                    const hsKey = Object.keys(row).find(k => k.match(/^HS\d Official$/) && !k.includes('ID'));
                    const label = hsKey ? row[hsKey] : 'Unknown';
                    dtExportProd.addRow([label, 'All Products', row['Trade Value'] || 0]);
                  });

                  Treemaps.drawChart(
                    dtExportProd,
                    {
                      ...options,
                      maxDepth: 1,
                      valueFormatter: function (value) {
                        return nFormatter(value, 2, '$');
                      },
                    },
                    container.querySelector('.chart__wrapper'),
                  );
                } else if (dataSourceType == 'OEC::product_share') {
                  const dtShare = new window.google.visualization.DataTable();
                  dtShare.addColumn('string', 'Product');
                  dtShare.addColumn('string', 'Parent');
                  dtShare.addColumn('number', 'Trade Value');
                  dtShare.addColumn('number', 'Market Share (%)');

                  dtShare.addRow(['All Products', null, 0, 0]);

                  data.data.forEach((row) => {
                    const hsKey = Object.keys(row).find(k => k.match(/^HS\d Official$/) && !k.includes('ID'));
                    const name = hsKey ? row[hsKey] : 'Unknown';
                    const share = row['Share'] || 0;
                    const tradeValue = row['Trade Value'] || 0;
                    const label = name + ' (' + share.toFixed(2) + '%)';
                    dtShare.addRow([label, 'All Products', tradeValue, share]);
                  });

                  Treemaps.drawChart(
                    dtShare,
                    {
                      ...options,
                      maxDepth: 1,
                      valueFormatter: function (value) {
                        return nFormatter(value, 2, '$');
                      },
                    },
                    container.querySelector('.chart__wrapper'),
                  );
                } else if (dataSourceType == 'OEC::digital_trade') {
                  const dtDigital = new window.google.visualization.DataTable();
                  dtDigital.addColumn('string', 'Firm');
                  dtDigital.addColumn('string', 'Parent');
                  dtDigital.addColumn('number', 'Digital Exports Value');

                  dtDigital.addRow(['All Firms', null, 0]);

                  const digData = data.data
                    .sort((a, b) => b['Trade Value HQ'] - a['Trade Value HQ'])
                    .slice(0, 15);

                  digData.forEach((row) => {
                    dtDigital.addRow([
                      row['Firm / App Developer'] || 'Unknown',
                      'All Firms',
                      row['Trade Value HQ'] || 0,
                    ]);
                  });

                  Treemaps.drawChart(
                    dtDigital,
                    {
                      ...options,
                      maxDepth: 2,
                      valueFormatter: function (value) {
                        return nFormatter(value, 2, '$');
                      },
                    },
                    container.querySelector('.chart__wrapper'),
                  );
                } else if (dataSourceType == 'OEC::digital_destinations') {
                  const dtDigDest = new window.google.visualization.DataTable();
                  dtDigDest.addColumn('string', 'Country');
                  dtDigDest.addColumn('string', 'Parent');
                  dtDigDest.addColumn('number', 'Digital Exports Value');

                  dtDigDest.addRow(['World', null, 0]);

                  const ddData = data.data
                    .sort((a, b) => b['Trade Value HQ'] - a['Trade Value HQ'])
                    .slice(0, 15);

                  ddData.forEach((row) => {
                    dtDigDest.addRow([
                      row['Importer Country Official'] || row['Country'] || 'Unknown',
                      'World',
                      row['Trade Value HQ'] || 0,
                    ]);
                  });

                  Treemaps.drawChart(
                    dtDigDest,
                    {
                      ...options,
                      maxDepth: 2,
                      valueFormatter: function (value) {
                        return nFormatter(value, 2, '$');
                      },
                    },
                    container.querySelector('.chart__wrapper'),
                  );
                }
              }
            })
            .catch((e) => {
              console.error(e);
              const wrapper = container.querySelector('.chart__wrapper');
              if (wrapper)
                wrapper.innerHTML =
                  '<p class="chart__error">No data available</p>';
            });
        } else if (dataSourceType == 'spreadsheet') {
          const query = new window.google.visualization.Query(
            dataSource,
            queryOptions,
          );

          query.send((response) => {
            if (response.isError()) {
              console.error(
                `Error in query: ${response.getMessage()} ${response.getDetailedMessage()}`,
              );
              return;
            }

            const data = response.getDataTable();

            Treemaps.drawChart(
              data,
              options,
              container.querySelector('.chart__wrapper'),
            );
          });
        }
      }
    });
  },
  drawChart(dataTable, options, wrapperElement) {
    if (dataTable && wrapperElement) {
      const chart = new window.google.visualization.TreeMap(wrapperElement);

      window.google.visualization.events.addListener(chart, 'ready', () => {
        //Append double-tap info on touch devices
        if (isTouchDevice()) {
          const info = document.createElement('span');
          info.classList = 'treemap-chart__info';
          info.innerText = 'Double tap to zoom in';
          wrapperElement.appendChild(info);
        }

        //Append back button
        const back = document.createElement('span');
        back.classList = 'treemap-chart__back-btn';
        back.innerHTML = '<i class="icon-chevron-left"></i>Back';

        back.addEventListener('click', () => {
          chart.goUpAndDraw();
          const current = chart.getSelection();
          if (current.length > 0 && current[0] && current[0].row < 1)
            wrapperElement.classList.remove('zoomed-in');
        });

        wrapperElement.appendChild(back);
      });

      window.google.visualization.events.addListener(chart, 'drilldown', () => {
        wrapperElement.classList.add('zoomed-in');
      });

      const tooltipValueFormatter =
        typeof options.valueFormatter === 'function'
          ? options.valueFormatter
          : (size) => size.toLocaleString();

      let chartConfig = {
        fontFamily: '"ARSMaquettePro", Helvetica, Arial, sans-serif',
        height: 400,
        showScale: options.showScale ?? false,
        headerHeight: options.showHeader ? 40 : 0,
        headerColor: options.headerColor ?? '#f7f7f7',
        minColor: options.minColor ?? '#ED7A5B',
        midColor: options.midColor ?? '#EBBF47',
        maxColor: options.maxColor ?? '#00BE9C',
        maxDepth: options.maxDepth ?? 1,
        ...(isTouchDevice() && {
          eventsConfig: {
            drilldown: ['dblclick'],
          },
        }),
        generateTooltip: (row, size) => {
          const label = dataTable.getColumnLabel(2);

          if (!label) return;

          return (
            '<div class="treemap-chart__tooltip">' +
            '<b>' +
            dataTable.getValue(row, 0) +
            '</b>' +
            '<br>' +
            dataTable.getColumnLabel(2) +
            ': ' +
            '<b>' +
            tooltipValueFormatter(size) +
            '</b>' +
            '</div>'
          );
        },
      };

      if (options.usePageColor) {
        chartConfig = {
          ...chartConfig,
          fontColor: initialColors.dark_5,
          headerColor: initialColors.light_7,
          minColor: initialColors.light_6,
          midColor: initialColors.light_4,
          maxColor: initialColors.light_1,
        };
      }

      chart.draw(dataTable, chartConfig);
    }
  },
};

export default Treemaps;
